import { useEffect, useMemo, useState } from 'react';
import {
  ActionIcon,
  Alert,
  Badge,
  Box,
  Button,
  Checkbox,
  FileButton,
  Group,
  Loader,
  Paper,
  Select,
  SimpleGrid,
  Stack,
  Text,
  Title,
  UnstyledButton,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  IconExternalLink,
  IconPhoto,
  IconPhotoPlus,
  IconRefresh,
  IconSparkles,
  IconStarFilled,
  IconTrash,
  IconX,
} from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  RENDER_MODEL_CODES,
  RENDER_MODELS,
  RENDER_STYLES,
  type RenderModelCode,
  type RenderStyleCode,
} from '@erp/shared';
import { ApiError } from '../api/client';
import {
  isRenderPending,
  renderSeriesApi,
  rendersApi,
  useRenderImage,
  type RenderSeries,
  type RoomRender,
} from '../api/renders';
import { WishesInput, wishesTooLong } from './WishesInput';

const STYLE_OPTIONS = RENDER_STYLES.map((s) => ({ value: s.code, label: s.label }));
const STYLE_LABELS = Object.fromEntries(RENDER_STYLES.map((s) => [s.code, s.label])) as Record<string, string>;
const MODEL_OPTIONS = RENDER_MODELS.map((m) => ({ value: m.code, label: m.label }));
const MODEL_LABELS = Object.fromEntries(RENDER_MODELS.map((m) => [m.code, m.label])) as Record<string, string>;
const MODEL_NOTES = Object.fromEntries(RENDER_MODELS.map((m) => [m.code, m.note])) as Record<string, string>;

/** Совпадает с MAX_SERIES_FRAMES в API — чтобы не загружать лишнее и не получать отказ на десятом фото. */
const MAX_SERIES_FRAMES = 10;
/** Совпадает с MAX_UPLOADS_PER_ROOM в API. */
const MAX_OWN_PHOTOS = 20;
const POLL_MS = 3_000;

/**
 * По умолчанию для серий — Nano Banana Pro: из доступных моделей она точнее
 * всех переносит мебель и отделку со второй картинки. Запоминается отдельно
 * от модели для 3D-вида — там у прораба может быть другая привычка.
 */
const DEFAULT_SERIES_MODEL: RenderModelCode = 'nano-banana-pro';
const MODEL_STORAGE_KEY = 'erp.render.seriesModel';

function loadModel(): RenderModelCode {
  try {
    const saved = localStorage.getItem(MODEL_STORAGE_KEY);
    if (saved && (RENDER_MODEL_CODES as readonly string[]).includes(saved)) return saved as RenderModelCode;
  } catch {
    // Хранилище недоступно (приватный режим) — берём модель по умолчанию.
  }
  return DEFAULT_SERIES_MODEL;
}

function saveModel(model: RenderModelCode): void {
  try {
    localStorage.setItem(MODEL_STORAGE_KEY, model);
  } catch {
    // Не запомнили — выбор действует до перезагрузки страницы.
  }
}

/** Нейросети больше 2K не нужно, а фото с телефона — 12+ Мп и несколько мегабайт. */
const PHOTO_MAX_SIDE = 2048;
/** data URL до ~2 МБ картинки: запрос к API ограничен 3 МБ с учётом base64. */
const MAX_DATA_URL_LENGTH = 2.6 * 1024 * 1024;

/**
 * Уменьшает фото в браузере до отправки: пачка с телефона по мобильному
 * интернету иначе грузилась бы минутами. createImageBitmap учитывает
 * EXIF-поворот — вертикальное фото не ляжет на бок.
 */
async function compressPhoto(file: File): Promise<string> {
  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
  } catch {
    // Чаще всего HEIC с iPhone, открытый не в Safari.
    throw new Error(`Не удалось открыть «${file.name}» — сохраните фото в JPEG и выберите снова`);
  }
  const scale = Math.min(1, PHOTO_MAX_SIDE / Math.max(bitmap.width, bitmap.height));
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(bitmap.width * scale);
  canvas.height = Math.round(bitmap.height * scale);
  canvas.getContext('2d')!.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  bitmap.close();
  for (const quality of [0.85, 0.7, 0.55]) {
    const dataUrl = canvas.toDataURL('image/jpeg', quality);
    if (dataUrl.length <= MAX_DATA_URL_LENGTH) return dataUrl;
  }
  throw new Error(`Фото «${file.name}» слишком большое`);
}

function errorMessage(err: unknown, fallback: string): string {
  return err instanceof ApiError || err instanceof Error ? err.message : fallback;
}

function plural(n: number, one: string, few: string, many: string): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return one;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return few;
  return many;
}

function usePreviews(files: File[]): string[] {
  const urls = useMemo(() => files.map((f) => URL.createObjectURL(f)), [files]);
  useEffect(() => () => urls.forEach((u) => URL.revokeObjectURL(u)), [urls]);
  return urls;
}

/** Новая серия: фото пачкой, общий стиль и пожелания. Сразу после загрузки запускается эталон. */
function NewSeriesForm({ estimateId, roomId }: { estimateId: string; roomId: string }) {
  const queryClient = useQueryClient();
  const [files, setFiles] = useState<File[]>([]);
  const [mainIndex, setMainIndex] = useState(0);
  const [model, setModel] = useState<RenderModelCode>(loadModel);
  const [style, setStyle] = useState<RenderStyleCode | null>(null);
  const [wishes, setWishes] = useState('');
  const [progress, setProgress] = useState<string | null>(null);
  const previews = usePreviews(files);

  function addFiles(picked: File[]) {
    const room = MAX_SERIES_FRAMES - files.length;
    if (picked.length > room) {
      notifications.show({ color: 'yellow', message: `В серии не больше ${MAX_SERIES_FRAMES} фото — лишние не добавлены` });
    }
    setFiles([...files, ...picked.slice(0, Math.max(room, 0))]);
  }

  function removeFile(index: number) {
    setFiles(files.filter((_, i) => i !== index));
    if (index === mainIndex) setMainIndex(0);
    else if (index < mainIndex) setMainIndex(mainIndex - 1);
  }

  const start = useMutation({
    mutationFn: async () => {
      // Главное фото идёт первым — сервер делает эталоном первое загруженное.
      const ordered = [files[mainIndex]!, ...files.filter((_, i) => i !== mainIndex)];
      // Сжимаем всё до создания серии: если какое-то фото не открывается,
      // пустая серия в галерее не нужна.
      const images: string[] = [];
      for (const [i, file] of ordered.entries()) {
        setProgress(`Подготовка фото ${i + 1} из ${ordered.length}…`);
        images.push(await compressPhoto(file));
      }
      const { series } = await renderSeriesApi.create(estimateId, {
        roomId,
        model,
        style,
        wishes: wishes.trim() || null,
      });
      let referenceId: string | null = null;
      for (const [i, image] of images.entries()) {
        setProgress(`Загрузка фото ${i + 1} из ${images.length}…`);
        const { render } = await renderSeriesApi.addPhoto(series.id, image);
        referenceId ??= render.id;
      }
      setProgress('Запуск эталона…');
      await rendersApi.generate(referenceId!);
    },
    onSuccess: () => {
      setFiles([]);
      setMainIndex(0);
      setWishes('');
    },
    onError: (err) => notifications.show({ color: 'red', message: errorMessage(err, 'Не удалось создать серию') }),
    onSettled: () => {
      setProgress(null);
      // И при ошибке: серия могла успеть создаться наполовину — её видно и можно удалить.
      void queryClient.invalidateQueries({ queryKey: ['renders', estimateId] });
      void queryClient.invalidateQueries({ queryKey: ['render-series', estimateId] });
    },
  });

  return (
    <Paper withBorder radius={0} p="sm">
      <Stack gap="xs">
        <Group align="flex-end" gap="xs" wrap="wrap">
          <Select
            size="xs"
            label="Нейросеть"
            data={MODEL_OPTIONS}
            value={model}
            onChange={(v) => {
              if (!v) return;
              setModel(v as RenderModelCode);
              saveModel(v as RenderModelCode);
            }}
            allowDeselect={false}
            title={MODEL_NOTES[model]}
            renderOption={({ option }) => (
              <div>
                <Text size="xs">{option.label}</Text>
                <Text size="xs" c="dimmed">
                  {MODEL_NOTES[option.value]}
                </Text>
              </div>
            )}
            comboboxProps={{ width: 300, position: 'bottom-start' }}
            w={170}
          />
          <Select
            size="xs"
            label="Стиль"
            placeholder="Без стиля"
            data={STYLE_OPTIONS}
            value={style}
            onChange={(v) => setStyle(v as RenderStyleCode | null)}
            clearable
            w={200}
          />
          <WishesInput value={wishes} onChange={setWishes} model={model} disabled={start.isPending} />
        </Group>

        {files.length > 0 && (
          <>
            <Text size="xs" c="dimmed">
              Нажмите на фото, чтобы сделать его главным: по нему нейросеть придумает дизайн, остальные ракурсы оформит
              так же. Лучше всего подходит кадр, где видно бо́льшую часть комнаты.
            </Text>
            <SimpleGrid cols={{ base: 3, xs: 4, sm: 5 }} spacing={6}>
              {previews.map((url, i) => (
                <Box key={url} pos="relative">
                  <UnstyledButton
                    onClick={() => setMainIndex(i)}
                    disabled={start.isPending}
                    aria-label={i === mainIndex ? 'Главное фото' : 'Сделать главным'}
                    style={{
                      display: 'block',
                      width: '100%',
                      aspectRatio: '1',
                      outline: i === mainIndex ? '3px solid var(--mantine-primary-color-filled)' : undefined,
                      outlineOffset: -3,
                    }}
                  >
                    <img src={url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                  </UnstyledButton>
                  {i === mainIndex && (
                    <Badge size="xs" radius={0} pos="absolute" top={4} left={4} leftSection={<IconStarFilled size={9} />}>
                      Главное
                    </Badge>
                  )}
                  {!start.isPending && (
                    <ActionIcon
                      size="xs"
                      variant="white"
                      color="dark"
                      radius={0}
                      pos="absolute"
                      top={4}
                      right={4}
                      onClick={() => removeFile(i)}
                      aria-label="Убрать фото"
                    >
                      <IconX size={12} />
                    </ActionIcon>
                  )}
                </Box>
              ))}
            </SimpleGrid>
          </>
        )}

        <Group gap="xs" wrap="wrap">
          <FileButton onChange={addFiles} accept="image/*" multiple disabled={start.isPending}>
            {(props) => (
              <Button
                {...props}
                size="xs"
                variant={files.length ? 'default' : 'filled'}
                leftSection={<IconPhotoPlus size={14} />}
                disabled={start.isPending || files.length >= MAX_SERIES_FRAMES}
              >
                {files.length ? 'Добавить фото' : 'Выбрать фото с объекта'}
              </Button>
            )}
          </FileButton>
          {files.length > 0 && (
            <Button
              size="xs"
              leftSection={<IconSparkles size={14} />}
              loading={start.isPending}
              disabled={wishesTooLong(wishes, model)}
              onClick={() => start.mutate()}
            >
              Сделать эталон
            </Button>
          )}
          {progress ? (
            <Text size="xs" c="dimmed">
              {progress}
            </Text>
          ) : (
            files.length > 0 && (
              <Text size="xs" c="dimmed">
                Сейчас — 1 генерация по главному фото.
                {files.length > 1 &&
                  ` Остальные ${files.length - 1} — после того, как вы утвердите эталон.`}
              </Text>
            )
          )}
        </Group>
      </Stack>
    </Paper>
  );
}

function FrameCard({
  render,
  isReference,
  canEdit,
  canRegenerate,
  canDelete,
  estimateId,
}: {
  render: RoomRender;
  isReference: boolean;
  canEdit: boolean;
  /** У обычного кадра — только когда готов эталон: без него оформлять не во что. */
  canRegenerate: boolean;
  canDelete: boolean;
  estimateId: string;
}) {
  const queryClient = useQueryClient();
  const [showSource, setShowSource] = useState(false);
  const isDone = render.status === 'DONE';
  const result = useRenderImage(isDone ? render.id : null, 'result', render.imageVersion);
  // Пока результата нет, в карточке — исходное фото: видно, какой ракурс ждёт.
  const source = useRenderImage(!isDone || showSource ? render.id : null, 'source');
  const url = isDone && !showSource ? result.url : source.url;
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['renders', estimateId] });

  const toggle = useMutation({
    mutationFn: (inProposal: boolean) => rendersApi.setInProposal(render.id, inProposal),
    onSuccess: invalidate,
    onError: (err) => notifications.show({ color: 'red', message: errorMessage(err, 'Не удалось изменить кадр') }),
  });
  const regenerate = useMutation({
    mutationFn: () => rendersApi.generate(render.id),
    onSuccess: () => setShowSource(false),
    onError: (err) => notifications.show({ color: 'red', message: errorMessage(err, 'Не удалось запустить генерацию') }),
    onSettled: invalidate,
  });
  const remove = useMutation({
    mutationFn: () => rendersApi.remove(render.id),
    onSuccess: () => {
      void invalidate();
      void queryClient.invalidateQueries({ queryKey: ['render-series', estimateId] });
    },
    onError: (err) => notifications.show({ color: 'red', message: errorMessage(err, 'Не удалось удалить кадр') }),
  });

  function confirmRegenerate() {
    const text =
      render.status === 'QUEUED'
        ? 'Сгенерировать кадр? Это 1 платная генерация.'
        : isReference
          ? 'Сделать другой вариант эталона? Текущий будет заменён (1 платная генерация). Уже готовые кадры останутся по старому эталону — их можно перегенерировать отдельно.'
          : 'Перегенерировать кадр? Текущая картинка будет заменена (1 платная генерация).';
    if (window.confirm(text)) regenerate.mutate();
  }

  const statusText =
    render.status === 'QUEUED'
      ? isReference
        ? 'Эталон не запущен'
        : 'Ждёт эталона'
      : isRenderPending(render)
        ? 'Генерируется, обычно до минуты…'
        : null;

  return (
    <Paper withBorder radius={0} p={0}>
      <Box style={{ aspectRatio: '4 / 3', background: 'var(--mantine-color-ledger-1)', position: 'relative' }}>
        {url && (
          <img
            src={url}
            alt={isReference ? 'Эталон серии' : 'Кадр серии'}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              display: 'block',
              // Исходное фото без результата приглушено — это ещё не дизайн.
              opacity: isDone ? 1 : 0.45,
            }}
          />
        )}
        {(statusText || render.status === 'FAILED' || (isDone && !url && !result.isError)) && (
          <Stack align="center" justify="center" gap={6} p="sm" pos="absolute" inset={0}>
            {(isRenderPending(render) || (isDone && !url)) && <Loader size="sm" />}
            {statusText && (
              <Text size="xs" fw={500} ta="center">
                {statusText}
              </Text>
            )}
            {render.status === 'FAILED' && (
              <Text size="xs" c="seal.6" fw={500} ta="center">
                {render.error ?? 'Не получилось'}
              </Text>
            )}
          </Stack>
        )}
        {isReference && (
          <Badge size="xs" radius={0} pos="absolute" top={6} left={6} leftSection={<IconStarFilled size={9} />}>
            Эталон
          </Badge>
        )}
        {showSource && isDone && (
          <Badge size="xs" radius={0} color="gray" pos="absolute" top={6} right={6}>
            Фото с объекта
          </Badge>
        )}
      </Box>
      <Group justify="space-between" wrap="nowrap" p="xs">
        <Checkbox
          size="xs"
          label="В КП"
          checked={render.inProposal}
          disabled={!canEdit || !isDone || toggle.isPending}
          onChange={(e) => toggle.mutate(e.currentTarget.checked)}
        />
        <Group gap={2} wrap="nowrap">
          {isDone && (
            <ActionIcon
              variant={showSource ? 'light' : 'subtle'}
              onClick={() => setShowSource(!showSource)}
              aria-label={showSource ? 'Показать дизайн' : 'Показать фото с объекта'}
              title={showSource ? 'Показать дизайн' : 'Было — фото с объекта'}
            >
              <IconPhoto size={14} />
            </ActionIcon>
          )}
          {url && (
            <ActionIcon component="a" href={url} target="_blank" variant="subtle" aria-label="Открыть в полном размере">
              <IconExternalLink size={14} />
            </ActionIcon>
          )}
          {canEdit && canRegenerate && !isRenderPending(render) && (
            <ActionIcon
              variant="subtle"
              loading={regenerate.isPending}
              onClick={confirmRegenerate}
              aria-label={render.status === 'QUEUED' ? 'Сгенерировать' : 'Перегенерировать'}
              title={render.status === 'QUEUED' ? 'Сгенерировать' : 'Перегенерировать'}
            >
              <IconRefresh size={14} />
            </ActionIcon>
          )}
          {canEdit && canDelete && (
            <ActionIcon
              variant="subtle"
              color="red"
              loading={remove.isPending}
              onClick={() => window.confirm('Удалить кадр из серии?') && remove.mutate()}
              aria-label="Удалить кадр"
            >
              <IconTrash size={14} />
            </ActionIcon>
          )}
        </Group>
      </Group>
    </Paper>
  );
}

function OwnPhotoCard({ render, canEdit, estimateId }: { render: RoomRender; canEdit: boolean; estimateId: string }) {
  const queryClient = useQueryClient();
  const { url } = useRenderImage(render.id, 'result', render.imageVersion);
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['renders', estimateId] });

  const toggle = useMutation({
    mutationFn: (inProposal: boolean) => rendersApi.setInProposal(render.id, inProposal),
    onSuccess: invalidate,
    onError: (err) => notifications.show({ color: 'red', message: errorMessage(err, 'Не удалось изменить фото') }),
  });
  const remove = useMutation({
    mutationFn: () => rendersApi.remove(render.id),
    onSuccess: invalidate,
    onError: (err) => notifications.show({ color: 'red', message: errorMessage(err, 'Не удалось удалить фото') }),
  });

  return (
    <Paper withBorder radius={0} p={0}>
      <Box style={{ aspectRatio: '4 / 3', background: 'var(--mantine-color-ledger-1)', position: 'relative' }}>
        {url ? (
          <img src={url} alt="Своё фото" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
        ) : (
          <Stack align="center" justify="center" pos="absolute" inset={0}>
            <Loader size="sm" />
          </Stack>
        )}
      </Box>
      <Group justify="space-between" wrap="nowrap" p="xs">
        <Checkbox
          size="xs"
          label="В КП"
          checked={render.inProposal}
          disabled={!canEdit || toggle.isPending}
          onChange={(e) => toggle.mutate(e.currentTarget.checked)}
        />
        <Group gap={2} wrap="nowrap">
          {url && (
            <ActionIcon component="a" href={url} target="_blank" variant="subtle" aria-label="Открыть в полном размере">
              <IconExternalLink size={14} />
            </ActionIcon>
          )}
          {canEdit && (
            <ActionIcon
              variant="subtle"
              color="red"
              loading={remove.isPending}
              onClick={() => window.confirm('Удалить фото?') && remove.mutate()}
              aria-label="Удалить фото"
            >
              <IconTrash size={14} />
            </ActionIcon>
          )}
        </Group>
      </Group>
    </Paper>
  );
}

/**
 * Свои фото в КП без нейросети: готовые объекты, дизайн-проект заказчика,
 * образцы материалов. Загрузка бесплатна, поэтому фото сразу отмечено «В КП».
 */
function OwnPhotos({
  estimateId,
  roomId,
  photos,
  canEdit,
}: {
  estimateId: string;
  roomId: string;
  photos: RoomRender[];
  canEdit: boolean;
}) {
  const queryClient = useQueryClient();
  const [progress, setProgress] = useState<string | null>(null);

  const upload = useMutation({
    mutationFn: async (picked: File[]) => {
      const room = MAX_OWN_PHOTOS - photos.length;
      if (picked.length > room) {
        notifications.show({ color: 'yellow', message: `Своих фото не больше ${MAX_OWN_PHOTOS} — лишние не добавлены` });
      }
      const files = picked.slice(0, Math.max(room, 0));
      for (const [i, file] of files.entries()) {
        setProgress(`Загрузка фото ${i + 1} из ${files.length}…`);
        await rendersApi.upload(estimateId, roomId, await compressPhoto(file));
      }
    },
    onError: (err) => notifications.show({ color: 'red', message: errorMessage(err, 'Не удалось загрузить фото') }),
    onSettled: () => {
      setProgress(null);
      // И при ошибке: часть фото могла успеть загрузиться.
      void queryClient.invalidateQueries({ queryKey: ['renders', estimateId] });
    },
  });

  if (!canEdit && photos.length === 0) return null;
  const ordered = [...photos].sort((a, b) => a.createdAt.localeCompare(b.createdAt));

  return (
    <Paper withBorder radius={0} p="sm">
      <Stack gap="xs">
        <Group justify="space-between" align="flex-start" wrap="wrap" gap="xs">
          <div style={{ minWidth: 0, flex: '1 1 240px' }}>
            <Text size="sm" fw={500}>
              Свои фото{photos.length > 0 && ` · ${photos.length}`}
            </Text>
            <Text size="xs" c="dimmed">
              Готовые объекты, дизайн-проект, образцы материалов — попадут в КП как есть, без нейросети.
            </Text>
          </div>
          {canEdit && (
            <Group gap="xs" wrap="wrap">
              {progress && (
                <Text size="xs" c="dimmed">
                  {progress}
                </Text>
              )}
              <FileButton onChange={(files) => upload.mutate(files)} accept="image/*" multiple disabled={upload.isPending}>
                {(props) => (
                  <Button
                    {...props}
                    size="compact-xs"
                    variant="default"
                    leftSection={<IconPhotoPlus size={12} />}
                    loading={upload.isPending}
                    disabled={photos.length >= MAX_OWN_PHOTOS}
                  >
                    Добавить свои фото
                  </Button>
                )}
              </FileButton>
            </Group>
          )}
        </Group>
        {ordered.length > 0 && (
          <SimpleGrid cols={{ base: 1, xs: 2, md: 3 }} spacing="xs">
            {ordered.map((photo) => (
              <OwnPhotoCard key={photo.id} render={photo} canEdit={canEdit} estimateId={estimateId} />
            ))}
          </SimpleGrid>
        )}
      </Stack>
    </Paper>
  );
}

function SeriesBlock({
  series,
  frames,
  canEdit,
  estimateId,
}: {
  series: RenderSeries;
  frames: RoomRender[];
  canEdit: boolean;
  estimateId: string;
}) {
  const queryClient = useQueryClient();
  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['renders', estimateId] });
    void queryClient.invalidateQueries({ queryKey: ['render-series', estimateId] });
  };

  const reference = frames.find((f) => f.id === series.referenceId) ?? null;
  const others = frames
    .filter((f) => f.id !== series.referenceId)
    .sort((a, b) => a.createdAt.localeCompare(b.createdAt));
  const queued = others.filter((f) => f.status === 'QUEUED');
  const done = frames.filter((f) => f.status === 'DONE');
  const allInProposal = done.length > 0 && done.every((f) => f.inProposal);
  const referenceReady = reference?.status === 'DONE';

  const approve = useMutation({
    mutationFn: () => renderSeriesApi.approve(series.id),
    onSuccess: ({ failed }) => {
      if (failed > 0) {
        notifications.show({ color: 'red', message: `${failed} из кадров не удалось запустить — повторите их отдельно` });
      }
    },
    onError: (err) => notifications.show({ color: 'red', message: errorMessage(err, 'Не удалось запустить серию') }),
    onSettled: invalidate,
  });
  const generateReference = useMutation({
    mutationFn: () => rendersApi.generate(reference!.id),
    onError: (err) => notifications.show({ color: 'red', message: errorMessage(err, 'Не удалось запустить эталон') }),
    onSettled: invalidate,
  });
  const toggleAll = useMutation({
    mutationFn: (inProposal: boolean) => renderSeriesApi.setInProposal(series.id, inProposal),
    onSettled: invalidate,
    onError: (err) => notifications.show({ color: 'red', message: errorMessage(err, 'Не удалось изменить серию') }),
  });
  const remove = useMutation({
    mutationFn: () => renderSeriesApi.remove(series.id),
    onSettled: invalidate,
    onError: (err) => notifications.show({ color: 'red', message: errorMessage(err, 'Не удалось удалить серию') }),
  });
  const addPhotos = useMutation({
    mutationFn: async (files: File[]) => {
      for (const file of files) await renderSeriesApi.addPhoto(series.id, await compressPhoto(file));
    },
    onError: (err) => notifications.show({ color: 'red', message: errorMessage(err, 'Не удалось добавить фото') }),
    onSettled: invalidate,
  });

  function confirmApprove() {
    const n = queued.length;
    const what = plural(n, 'кадр', 'кадра', 'кадров');
    const paid = plural(n, 'платная генерация', 'платные генерации', 'платных генераций');
    if (window.confirm(`Оформить ${n} ${what} по эталону? Это ${n} ${paid}.`)) {
      approve.mutate();
    }
  }

  const caption = [MODEL_LABELS[series.model], series.style ? STYLE_LABELS[series.style] : null, series.wishes]
    .filter(Boolean)
    .join(' · ');
  const ordered = reference ? [reference, ...others] : others;

  return (
    <Stack gap="xs">
      <Group justify="space-between" align="flex-start" wrap="wrap" gap="xs">
        <div style={{ minWidth: 0, flex: '1 1 240px' }}>
          <Text size="sm" fw={500}>
            Серия от {new Date(series.createdAt).toLocaleDateString('ru-RU')} · {frames.length} фото
          </Text>
          {caption && (
            <Text size="xs" c="dimmed" lineClamp={2} title={caption}>
              {caption}
            </Text>
          )}
        </div>
        {canEdit && (
          <Group gap={6} wrap="wrap">
            <Button
              size="compact-xs"
              variant="default"
              disabled={done.length === 0}
              loading={toggleAll.isPending}
              onClick={() => toggleAll.mutate(!allInProposal)}
            >
              {allInProposal ? 'Убрать серию из КП' : 'Вся серия в КП'}
            </Button>
            <FileButton
              onChange={(files) => addPhotos.mutate(files.slice(0, MAX_SERIES_FRAMES - frames.length))}
              accept="image/*"
              multiple
            >
              {(props) => (
                <Button
                  {...props}
                  size="compact-xs"
                  variant="default"
                  leftSection={<IconPhotoPlus size={12} />}
                  loading={addPhotos.isPending}
                  disabled={frames.length >= MAX_SERIES_FRAMES}
                >
                  Ракурсы
                </Button>
              )}
            </FileButton>
            <ActionIcon
              variant="subtle"
              color="red"
              loading={remove.isPending}
              onClick={() => window.confirm('Удалить серию целиком, со всеми фото и картинками?') && remove.mutate()}
              aria-label="Удалить серию"
            >
              <IconTrash size={14} />
            </ActionIcon>
          </Group>
        )}
      </Group>

      {reference && isRenderPending(reference) && (
        <Alert radius={0} color="blue" p="xs">
          <Text size="xs">
            Нейросеть оформляет главное фото. Посмотрите эталон: если нравится — остальные ракурсы оформим так же, если
            нет — сделаем другой вариант.
          </Text>
        </Alert>
      )}
      {canEdit && reference && (reference.status === 'QUEUED' || reference.status === 'FAILED') && (
        <Alert radius={0} color="yellow" p="xs">
          <Group justify="space-between" gap="xs" wrap="wrap">
            <Text size="xs">
              {reference.status === 'FAILED' ? 'Эталон не получился.' : 'Эталон ещё не запускали.'} Без него остальные кадры
              не во что оформлять.
            </Text>
            <Button
              size="compact-xs"
              leftSection={<IconSparkles size={12} />}
              loading={generateReference.isPending}
              onClick={() => generateReference.mutate()}
            >
              {reference.status === 'FAILED' ? 'Повторить эталон' : 'Сделать эталон'}
            </Button>
          </Group>
        </Alert>
      )}
      {canEdit && referenceReady && queued.length > 0 && (
        <Alert radius={0} color="green" p="xs">
          <Group justify="space-between" gap="xs" wrap="wrap">
            <Text size="xs">
              Эталон готов. Если дизайн нравится — оформим остальные {queued.length} фото в том же стиле, с той же мебелью
              и отделкой. Если нет — нажмите ↻ на эталоне, чтобы получить другой вариант.
            </Text>
            <Button size="compact-xs" leftSection={<IconSparkles size={12} />} loading={approve.isPending} onClick={confirmApprove}>
              Оформить остальные ({queued.length})
            </Button>
          </Group>
        </Alert>
      )}

      <SimpleGrid cols={{ base: 1, xs: 2, md: 3 }} spacing="xs">
        {ordered.map((frame) => {
          const isReference = frame.id === series.referenceId;
          return (
            <FrameCard
              key={frame.id}
              render={frame}
              isReference={isReference}
              canEdit={canEdit}
              canRegenerate={isReference || referenceReady}
              canDelete={!isReference || frames.length === 1}
              estimateId={estimateId}
            />
          );
        })}
      </SimpleGrid>
    </Stack>
  );
}

/**
 * Дизайн по фото с объекта: прораб снимает комнату с нескольких ракурсов и
 * получает серию картинок в одном оформлении — с одинаковой мебелью и
 * отделкой. Сначала нейросеть оформляет главное фото (эталон), после
 * утверждения остальные ракурсы генерируются с эталоном второй картинкой.
 */
export function PhotoSeriesPanel({
  estimateId,
  roomId,
  canEdit,
}: {
  estimateId: string | undefined;
  roomId: string;
  canEdit: boolean;
}) {
  // Тот же ключ, что у галереи 3D-вида: один список визуализаций помещения, один опрос.
  const renders = useQuery({
    queryKey: ['renders', estimateId, roomId],
    queryFn: () => rendersApi.list(estimateId!, roomId),
    enabled: !!estimateId,
    refetchInterval: (query) => (query.state.data?.items.some(isRenderPending) ? POLL_MS : false),
  });
  const seriesList = useQuery({
    queryKey: ['render-series', estimateId, roomId],
    queryFn: () => renderSeriesApi.list(estimateId!, roomId),
    enabled: !!estimateId,
  });

  const framesBySeries = new Map<string, RoomRender[]>();
  const ownPhotos: RoomRender[] = [];
  for (const render of renders.data?.items ?? []) {
    if (render.uploaded) ownPhotos.push(render);
    if (!render.seriesId) continue;
    const list = framesBySeries.get(render.seriesId) ?? [];
    list.push(render);
    framesBySeries.set(render.seriesId, list);
  }

  if (!estimateId) {
    return (
      <Text size="sm" c="dimmed">
        Сохраните смету — фото и визуализации хранятся вместе с ней.
      </Text>
    );
  }

  const items = seriesList.data?.items ?? [];

  return (
    <Stack gap="md">
      <div>
        <Title order={5}>Новая серия</Title>
        <Text size="xs" c="dimmed">
          Сфотографируйте комнату с нескольких точек — из углов и от двери. Нейросеть оформит главное фото, а после вашего
          «нравится» — остальные ракурсы с той же мебелью и отделкой. Окна и двери остаются на своих местах.
        </Text>
      </div>

      {canEdit && <NewSeriesForm estimateId={estimateId} roomId={roomId} />}

      {renders.isSuccess && <OwnPhotos estimateId={estimateId} roomId={roomId} photos={ownPhotos} canEdit={canEdit} />}

      {(renders.isLoading || seriesList.isLoading) && (
        <Group justify="center" p="md">
          <Loader size="sm" />
        </Group>
      )}

      {items.map((series) => (
        <Paper key={series.id} withBorder radius={0} p="sm">
          <SeriesBlock series={series} frames={framesBySeries.get(series.id) ?? []} canEdit={canEdit} estimateId={estimateId} />
        </Paper>
      ))}
    </Stack>
  );
}
